import React, { useMemo, useState } from 'react';
import {
  Container,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import UpdateIcon from '@mui/icons-material/Update';
import LabelIcon from '@mui/icons-material/Label';
import { toast } from 'react-toastify';
import {
  useGetVocabularyMemoriesQuery,
  useGetVocabularyMemoryStatsQuery,
  useDeleteVocabularyMemoryByIdMutation,
} from '@/store/api/user/vocabularyApi';
import TextToSpeechButton from '@/components/common/TextToSpeechButton';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'alpha', label: 'A → Z' },
  { value: 'alpha-desc', label: 'Z → A' },
];

const SOURCE_COLORS = {
  exercise: 'primary',
  assignment: 'success',
  practice: 'warning',
  review: 'secondary',
};

const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const MemoriesPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 350);
  const [sort, setSort] = useState('recent');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const limit = 12;

  const queryArgs = useMemo(() => {
    const params = { page, limit, sort };
    if (search?.trim()) params.search = search.trim();
    return params;
  }, [page, limit, sort, search]);

  const { data, isFetching, isError, error, refetch } =
    useGetVocabularyMemoriesQuery(queryArgs);

  const { data: statsData } = useGetVocabularyMemoryStatsQuery();

  const [deleteById, { isLoading: deleting }] =
    useDeleteVocabularyMemoryByIdMutation();

  const items = data?.data || [];
  const pagination = data?.pagination;
  const stats = statsData?.data;

  // Reset to page 1 whenever search or sort changes.
  React.useEffect(() => {
    setPage(1);
  }, [search, sort]);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteById(pendingDelete._id).unwrap();
      toast.success('Removed from Memories');
      setPendingDelete(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to remove word');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #e3f2fd 100%)',
        py: 2,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #7C4DFF 0%, #5E35B1 100%)',
            color: 'white',
            boxShadow: '0 10px 30px rgba(94, 53, 177, 0.25)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <BookmarksIcon sx={{ fontSize: 36 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                My Memories
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Words you saved while practicing. Highlight any word in an
                exercise, assignment, or review to add more.
              </Typography>
            </Box>
          </Box>

          {stats && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
                />
              }
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Total words
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {stats.totalWords ?? 0}
                </Typography>
              </Box>
              {stats.bySource &&
                Object.entries(stats.bySource).map(([key, count]) => (
                  <Box key={key}>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.85, textTransform: 'capitalize' }}
                    >
                      From {key}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {count}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          )}
        </Paper>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search words, translations, or notes…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchInput('')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="memories-sort-label">
                  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                    <SortIcon fontSize="small" /> Sort
                  </Box>
                </InputLabel>
                <Select
                  labelId="memories-sort-label"
                  value={sort}
                  label="Sort"
                  onChange={(e) => setSort(e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Content */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={refetch}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            {error?.data?.message || 'Failed to load your saved words.'}
          </Alert>
        )}

        {isFetching && items.length === 0 ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Paper
            sx={{
              p: 6,
              borderRadius: 3,
              textAlign: 'center',
              background: '#fff',
            }}
          >
            <TranslateIcon sx={{ fontSize: 56, color: '#90caf9', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              No saved words yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Highlight any English word inside an exercise, assignment, or
              review screen and tap "Save to Memories" to build your list.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {items.map((m) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={m._id}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid rgba(124, 77, 255, 0.15)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(124, 77, 255, 0.18)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => setSelectedItem(m)}
                    sx={{ height: '100%', alignItems: 'stretch' }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        gap: 1,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                              color: '#5E35B1',
                              wordBreak: 'break-word',
                            }}
                          >
                            {m.word}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#37474f', mt: 0.25 }}
                          >
                            {m.translation}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.25}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Pronounce">
                            <span>
                              <TextToSpeechButton
                                text={m.word}
                                size="small"
                                color="primary"
                                sx={{ p: 0.5 }}
                              />
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={() => setPendingDelete(m)}
                              sx={{ color: '#e53935' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {m.context && (
                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1,
                            bgcolor: 'rgba(124, 77, 255, 0.06)',
                            borderLeft: '3px solid #7C4DFF',
                            borderRadius: 1,
                          }}
                        >
                          <Box display="flex" gap={0.5}>
                            <FormatQuoteIcon
                              sx={{ color: '#B39DDB', fontSize: 16, flexShrink: 0 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#546e7a',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {m.context}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ flex: 1 }} />

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        mt={1}
                      >
                        {m.source?.type ? (
                          <Chip
                            size="small"
                            color={SOURCE_COLORS[m.source.type] || 'default'}
                            variant="outlined"
                            label={
                              m.source.label
                                ? m.source.label
                                : m.source.type.charAt(0).toUpperCase() +
                                  m.source.type.slice(1)
                            }
                            sx={{ maxWidth: '75%' }}
                          />
                        ) : (
                          <span />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {m.createdAt
                            ? new Date(m.createdAt).toLocaleDateString()
                            : ''}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              page={pagination.page}
              count={pagination.totalPages}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Container>

      {/* Detail dialog */}
      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedItem && (
          <>
            <DialogTitle sx={{ pr: 6, pb: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#5E35B1', wordBreak: 'break-word' }}
                >
                  {selectedItem.word}
                </Typography>
                <TextToSpeechButton
                  text={selectedItem.word}
                  size="small"
                  color="primary"
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ color: '#37474f', mt: 0.5 }}
              >
                {selectedItem.translation}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => setSelectedItem(null)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {selectedItem.context && (
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: '#5E35B1', fontWeight: 700 }}
                    >
                      Context
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        bgcolor: 'rgba(124, 77, 255, 0.07)',
                        borderLeft: '3px solid #7C4DFF',
                        borderRadius: 1,
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      <FormatQuoteIcon
                        sx={{ color: '#B39DDB', fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: '#37474f', lineHeight: 1.6 }}
                      >
                        {selectedItem.context}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedItem.source?.type && (
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: '#5E35B1', fontWeight: 700 }}
                    >
                      Source
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Chip
                        size="small"
                        color={
                          SOURCE_COLORS[selectedItem.source.type] || 'default'
                        }
                        variant="outlined"
                        icon={<LabelIcon />}
                        label={
                          selectedItem.source.type.charAt(0).toUpperCase() +
                          selectedItem.source.type.slice(1)
                        }
                      />
                      {/* {selectedItem.source.label && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedItem.source.label}
                        </Typography>
                      )} */}
                    </Box>
                  </Box>
                )}

                <Divider />

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  divider={
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ display: { xs: 'none', sm: 'block' } }}
                    />
                  }
                >
                  {selectedItem.createdAt && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <EventIcon fontSize="small" sx={{ color: '#78909c' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Saved on
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedItem.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {selectedItem.updatedAt &&
                    selectedItem.updatedAt !== selectedItem.createdAt && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <UpdateIcon
                          fontSize="small"
                          sx={{ color: '#78909c' }}
                        />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Last updated
                          </Typography>
                          <Typography variant="body2">
                            {new Date(selectedItem.updatedAt).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  setPendingDelete(selectedItem);
                  setSelectedItem(null);
                }}
              >
                Remove
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={() => setSelectedItem(null)}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => !deleting && setPendingDelete(null)}
      >
        <DialogTitle>Remove from Memories?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove <b>{pendingDelete?.word}</b> from your saved
            words. You can always save it again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoriesPage;
